import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthProvider';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../utils/translations';
import { supabase } from '../utils/supabase';
import { Menu, CartItem } from '../types';
import { useNavigate } from 'react-router-dom';

const MenuOrder: React.FC = () => {
  const [menuItems, setMenuItems] = useState<Menu[]>([]);
  const [filteredItems, setFilteredItems] = useState<Menu[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [quantities, setQuantities] = useState<{[key: number]: number}>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const { user, signOut } = useAuth();
  const { language, setLanguage, isRTL } = useLanguage();
  const t = (key: string) => getTranslation(key as any, language);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMenuItems();
    fetchCategories();
    fetchCartItems();
  }, []);

  useEffect(() => {
    filterItems();
  }, [menuItems, searchTerm, selectedCategory]);

  const fetchCategories = async () => {
    const { data, error } = await supabase
      .from('habib_menu_categories')
      .select('*')
      .order('name', { ascending: true });
    
    if (error) {
      console.error('Error fetching categories:', error);
    } else if (data) {
      setCategories(data);
    }
  };

  const fetchMenuItems = async () => {
    const { data, error } = await supabase
      .from('habib_menu')
      .select(`
        *,
        menu_category:habib_menu_categories(id, name, description)
      `)
      .eq('is_available', true) // Only show available items
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching menu:', error);
    } else if (data) {
      setMenuItems(data);
      setFilteredItems(data);
    }
  };

  const filterItems = () => {
    let filtered = menuItems.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === null || item.category_id === selectedCategory;
      return matchesSearch && matchesCategory;
    });
    setFilteredItems(filtered);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);

  const fetchCartItems = async () => {
    const { data, error } = await supabase
      .from('habib_cart')
      .select(`
        *,
        menu:habib_menu(*),
        product:habib_products(*)
      `)
      .eq('user_id', user?.id);
    
    if (error) {
      console.error('Error fetching cart:', error);
    } else if (data) {
      setCartItems(data);
    }
  };

  const updateQuantity = async (menuId: number, newQuantity: number) => {
    setQuantities(prev => ({ ...prev, [menuId]: newQuantity }));
    
    if (newQuantity === 0) {
      const existingItem = cartItems.find(item => item.menu_id === menuId);
      if (existingItem) {
        const { error } = await supabase.from('habib_cart').delete().eq('id', existingItem.id);
        if (error) {
          alert('Failed to remove item: ' + error.message);
          return;
        }
        setCartItems(cartItems.filter(item => item.id !== existingItem.id));
      }
      return;
    }
    
    const existingItem = cartItems.find(item => item.menu_id === menuId);
    
    if (existingItem) {
      const { data, error } = await supabase
        .from('habib_cart')
        .update({ quantity: newQuantity })
        .eq('id', existingItem.id)
        .select('*, menu:habib_menu(*)');
      
      if (error) {
        alert('Failed to update cart: ' + error.message);
        return;
      }
      
      if (data) {
        setCartItems(cartItems.map(item => 
          item.id === existingItem.id ? data[0] : item
        ));
      }
    } else {
      const { data, error } = await supabase
        .from('habib_cart')
        .insert([{ user_id: user?.id, menu_id: menuId, product_id: null, quantity: newQuantity }])
        .select('*, menu:habib_menu(*)');
      
      if (error) {
        alert('Failed to add to cart: ' + error.message);
        return;
      }
      
      if (data) {
        setCartItems([...cartItems, ...data]);
      }
    }
  };
  
  const getQuantity = (menuId: number) => {
    return quantities[menuId] || cartItems.find(item => item.menu_id === menuId)?.quantity || 0;
  };

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)'}}>
      {/* Navigation */}
      <nav className="bg-white/90 backdrop-blur-md shadow-soft" style={{borderBottom: '1px solid #05472A20'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/')}
                className="mr-4 p-2 hover:bg-green-50 rounded-xl transition-all"
                style={{color: '#05472A'}}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl ${isRTL ? 'ml-3' : 'mr-3'} flex items-center justify-center shadow-lg overflow-hidden`} style={{backgroundColor: '#05472A'}}>
                <img 
                  src="https://edu.almuwasholah.com/wp-content/uploads/2026/02/logo-habib-1.png"
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="text-lg sm:text-xl font-bold" style={{color: '#05472A'}}>{t('menuFoodDrink')}</h1>
            </div>
            <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 sm:space-x-4`}>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setLanguage('en')}
                  className={`px-2 py-1 rounded text-xs font-medium transition-all duration-300 ${
                    language === 'en' ? 'text-white' : 'text-gray-600 hover:text-gray-800'
                  }`}
                  style={language === 'en' ? {backgroundColor: '#05472A'} : {}}
                >
                  EN
                </button>
                <button 
                  onClick={() => setLanguage('ar')}
                  className={`px-2 py-1 rounded text-xs font-medium transition-all duration-300 ${
                    language === 'ar' ? 'text-white' : 'text-gray-600 hover:text-gray-800'
                  }`}
                  style={language === 'ar' ? {backgroundColor: '#05472A'} : {}}
                >
                  AR
                </button>
              </div>
              <span className="hidden md:block text-sm font-medium" style={{color: '#05472A'}}>{t('hi')}, {user?.username}</span>
              <button onClick={signOut} className="bg-white hover:bg-green-50 border px-4 py-2 sm:px-6 sm:py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium" style={{color: '#05472A', borderColor: '#05472A40'}}>
                <span className="hidden sm:inline">{t('signOut')}</span>
                <svg className="w-4 h-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:py-8 px-4 sm:px-6 lg:px-8">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md mx-auto">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('searchMenuItems')}
              className="w-full px-4 py-3 pl-12 pr-4 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300"
              style={{borderColor: '#05472A20'}}
            />
            <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Categories */}
        {categories.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 rounded-xl font-semibold whitespace-nowrap transition-all duration-300 ${
                  selectedCategory === null
                    ? 'text-white shadow-soft'
                    : 'bg-white border hover:bg-green-50'
                }`}
                style={selectedCategory === null ? {backgroundColor: '#05472A'} : {borderColor: '#05472A40', color: '#05472A'}}
              >
                {t('allCategories')}
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-xl font-semibold whitespace-nowrap transition-all duration-300 ${
                    selectedCategory === category.id
                      ? 'text-white shadow-soft'
                      : 'bg-white border hover:bg-green-50'
                  }`}
                  style={selectedCategory === category.id ? {backgroundColor: '#05472A'} : {borderColor: '#05472A40', color: '#05472A'}}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Menu Items Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {currentItems.map((item) => (
            <div key={item.id} className="bg-white rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 overflow-hidden group hover:scale-105 border" style={{borderColor: '#05472A20'}}>
              <div className="relative overflow-hidden">
                <img 
                  src={item.photo} 
                  alt={item.name}
                  className="w-full h-40 sm:h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-3 right-3">
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 border border-green-200" style={{color: '#05472A'}}>
                    {t('available')}
                  </span>
                </div>
                {item.menu_category && (
                  <div className="absolute top-3 left-3">
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-white border" style={{color: '#05472A', borderColor: '#05472A40'}}>
                      {item.menu_category.name}
                    </span>
                  </div>
                )}
              </div>
              <div className="p-4 sm:p-5">
                <h3 className="font-bold mb-2 text-sm sm:text-base line-clamp-2" style={{color: '#05472A'}}>{item.name}</h3>
                <p className="text-xs sm:text-sm mb-4 line-clamp-2" style={{color: '#05472A99'}}>{item.description}</p>
                
                {/* Quantity Controls */}
                <div className="flex items-center justify-center gap-3">
                  <button 
                    onClick={() => updateQuantity(item.id, Math.max(0, getQuantity(item.id) - 1))}
                    className="w-10 h-10 rounded-xl border-2 flex items-center justify-center hover:bg-green-50 transition-colors"
                    style={{borderColor: '#05472A40', color: '#05472A'}}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <span className="text-xl font-semibold w-8 text-center" style={{color: '#05472A'}}>
                    {getQuantity(item.id)}
                  </span>
                  <button 
                    onClick={() => updateQuantity(item.id, getQuantity(item.id) + 1)}
                    className="w-10 h-10 rounded-xl border-2 flex items-center justify-center hover:bg-green-50 transition-colors"
                    style={{borderColor: '#05472A40', color: '#05472A'}}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredItems.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{backgroundColor: '#05472A10'}}>
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: '#05472A99'}}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{color: '#05472A'}}>{t('noItemsFound')}</h3>
            <p style={{color: '#05472A99'}}>{t('tryAdjustingFilters')}</p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8 flex justify-center items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-xl border transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-50"
              style={{borderColor: '#05472A40', color: '#05472A'}}
            >
              ←
            </button>
            <span className="px-4 py-2 font-medium" style={{color: '#05472A'}}>
              {t('page')} {currentPage} {t('of')} {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-xl border transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-50"
              style={{borderColor: '#05472A40', color: '#05472A'}}
            >
              →
            </button>
          </div>
        )}
      </main>

      {/* Floating Cart Button */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <button 
          onClick={() => navigate('/cart')} 
          className="text-white px-8 py-4 rounded-full shadow-strong hover:shadow-xl transition-all duration-300 transform hover:scale-110 active:scale-95 flex items-center gap-4 min-w-[200px] justify-center"
          style={{backgroundColor: '#05472A'}}
        >
          <div className="relative">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 11-4 0v-6m4 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
            </svg>
            {cartItems.reduce((sum, item) => sum + item.quantity, 0) > 0 && (
              <span className={`absolute -top-2 ${isRTL ? '-left-2' : '-right-2'} bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold animate-pulse`}>
                {cartItems.reduce((sum, item) => sum + item.quantity, 0)}
              </span>
            )}
          </div>
          <span className="font-semibold text-lg">{t('viewCart')}</span>
        </button>
      </div>
    </div>
  );
};

export default MenuOrder;
